import axios from "axios";

// If REACT_APP_BACKEND_URL is missing (e.g. production build where the env
// var wasn't injected at build time), fall back to an empty string so that
// API_BASE becomes a relative "/api" path. Axios then issues same-origin
// requests against the current page host — which is what we want when the
// SPA and the FastAPI ingress live on the same domain.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

// Auth uses httpOnly cookies set by the server. We expose a no-op tokenStore
// shim so legacy callers don't crash; nothing sensitive is ever written to
// localStorage. Cookies travel via `withCredentials: true`.
export const tokenStore = {
  get: () => null,
  set: () => {},
  clear: () => {
    try { localStorage.removeItem("bsca_admin_token"); } catch (_e) { /* ignore */ }
  },
};

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

export function formatApiError(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ----- public read helpers -----
export const publicApi = {
  siteSettings: () => api.get("/public/site-settings").then((r) => r.data),
  page: (slug) => api.get(`/public/page/${slug}`).then((r) => r.data),
  books: () => api.get("/public/books").then((r) => r.data),
  book: (slug) => api.get(`/public/books/${slug}`).then((r) => r.data),
  founder: () => api.get("/public/founder").then((r) => r.data),
  foundingMembers: () => api.get("/public/founding-members").then((r) => r.data),
  domains: () => api.get("/public/research-domains").then((r) => r.data),
  questions: (domain) =>
    api
      .get("/public/research-questions", { params: domain ? { domain } : {} })
      .then((r) => r.data),
  programs: () => api.get("/public/programs").then((r) => r.data),
  media: () => api.get("/public/media").then((r) => r.data),
  legal: (slug) => api.get(`/public/legal/${slug}`).then((r) => r.data),
};

export const formsApi = {
  submit: (form_type, payload) =>
    api.post("/forms/submit", { form_type, payload }).then((r) => r.data),
};

export const adminApi = {
  login: (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  list: (col) => api.get(`/admin/collections/${col}`).then((r) => r.data),
  get: (col, id) =>
    api.get(`/admin/collections/${col}/${id}`).then((r) => r.data),
  create: (col, data) =>
    api.post(`/admin/collections/${col}`, { data }).then((r) => r.data),
  update: (col, id, data) =>
    api.put(`/admin/collections/${col}/${id}`, { data }).then((r) => r.data),
  remove: (col, id) =>
    api.delete(`/admin/collections/${col}/${id}`).then((r) => r.data),
  submissions: (form_type) =>
    api
      .get("/admin/submissions", { params: form_type ? { form_type } : {} })
      .then((r) => r.data),
  exportCsvUrl: (form_type) =>
    `${API_BASE}/admin/submissions/export.csv${
      form_type ? `?form_type=${encodeURIComponent(form_type)}` : ""
    }`,
  // helper for code paths that need to attach the Authorization header
  // (e.g. <a href> downloads). Returns null in cookie-only mode.
  bearerForFetch: () => null,
  deleteSubmission: (id) => api.delete(`/admin/submissions/${id}`).then((r) => r.data),
  patchSubmission: (id, body) => api.patch(`/admin/submissions/${id}`, body).then((r) => r.data),
  // Assets / Media library
  assetsList: (params) => api.get("/admin/assets", { params: params || {} }).then((r) => r.data),
  assetsUpload: (file, category) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category || "other");
    return api
      .post("/admin/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  assetsPatch: (id, body) => api.patch(`/admin/assets/${id}`, body).then((r) => r.data),
  assetsDelete: (id) => api.delete(`/admin/assets/${id}`).then((r) => r.data),
};

// Convert a stored asset URL (which may be a relative /api/uploads/... path,
// a relative /assets/... static path, or an absolute URL) into something the
// <img> tag can actually fetch from the frontend origin.
export function resolveAssetUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
}
